import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import CategoryDropdown from '../ui/CategoryDropdown';
import { Textarea } from '../ui/textarea';
import { inventoryAPI } from '../../services/api';import { cn } from '../../lib/utils';
import SuccessScreen from '../ui/SuccessScreen';
import BeautifiedErrorAlert from '../ui/BeautifiedErrorAlert';
import useServerValidation from '../../hooks/useServerValidation';
import {
  FileSpreadsheet,
  Upload,
  FileUp,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Plus,
  LayoutGrid,
  History,
  Info,
  Tag,
  Package,
  Calendar,
  Layers,
  ClipboardList,
  ArrowRight
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

import SKUPillInput from '../ui/SKUPillInput';

const AddModal = ({ onClose, onAdd, modalType, initialData = null }) => {
  const [step, setStep] = useState(initialData ? 'form' : (modalType === 'product' ? 'choice' : 'form'));
  const [bulkFile, setBulkFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  const [submitError, setSubmitError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [errors, setErrors] = useState({});
  const { applyServerErrors } = useServerValidation({ setErrors });

  // Success Redirection Timer
  useEffect(() => {
    let timer;
    if (isSuccess && countdown > 0) {
        timer = setInterval(() => setCountdown(c => c - 1), 1000);
    } else if (isSuccess && countdown === 0) {
        onClose();
    }
    return () => clearInterval(timer);
  }, [isSuccess, countdown, onClose]);

  // Form State
  const [formData, setFormData] = useState({
    categoryId: '',
    categoryName: '',
    productName: '',
    brand: '',
    productType: 'SERIALIZED',
    hsnCode: '',
    purchasePriceInr: '',
    sellingPriceInr: '',
    stockQty: 1,
    warrantyMonths: '',
    gstPercent: '',
    dealerSupplierName: '',
    purchaseDate: '',
    skuProductCodes: [],
    warrantyStartDate: '',
    warrantyEndDate: '',
    description: '',
    specification: '',
    locationRack: ''
  });

  const handleClearDraft = () => {
    if (window.confirm('Clear all fields? This action cannot be undone.')) {
        setFormData({
            categoryId: '',
            categoryName: '',
            productName: '',
            brand: '',
            productType: 'SERIALIZED',
            hsnCode: '',
            purchasePriceInr: '',
            sellingPriceInr: '',
            stockQty: 1,
            warrantyMonths: '',
            gstPercent: '',
            dealerSupplierName: '',
            purchaseDate: '',
            skuProductCodes: [],
            warrantyStartDate: '',
            warrantyEndDate: '',
            description: '',
            specification: '',
            locationRack: ''
        });
        setErrors({});
        setSubmitError(null);
    }
  };

  // Category Logic
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesPage, setCategoriesPage] = useState(0);
  const [hasMoreCategories, setHasMoreCategories] = useState(true);
  const [showInlineCategoryModal, setShowInlineCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const loadingRef = useRef(false);

  const fetchCategories = useCallback(async (page = 0, reset = false) => {
    if (loadingRef.current && !reset) return;
    loadingRef.current = true;
    setCategoriesLoading(true);
    try {
      const params = {
        pageNumber: page,
        pageSize: 15,
        sortBy: 'categoryName',
        sortOrder: 'ASC'
      };
      const response = await inventoryAPI.getCategories(params);
      const newCategories = response.data.content || response.data;
      const isLast = response.data.lastPage || response.data.last || newCategories.length < 15;
      setCategories(prev => reset ? newCategories : [...prev, ...newCategories]);
      setHasMoreCategories(!isLast);
      setCategoriesPage(page);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setCategoriesLoading(false);
      loadingRef.current = false;
    }
  }, []); // Remove categoriesLoading as dependency to break infinite loop

  useEffect(() => {
    if (modalType === 'product') {
      fetchCategories(0, true);
    }
  }, [modalType, fetchCategories]);

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData, setFormData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Auto-calculate warranty months if dates change
      if (name === 'warrantyStartDate' || name === 'warrantyEndDate') {
        const start = name === 'warrantyStartDate' ? value : newData.warrantyStartDate;
        const end = name === 'warrantyEndDate' ? value : newData.warrantyEndDate;
        if (start && end) {
          const sd = new Date(start);
          const ed = new Date(end);
          if (!isNaN(sd.getTime()) && !isNaN(ed.getTime())) {
            let months = (ed.getFullYear() - sd.getFullYear()) * 12 + (ed.getMonth() - sd.getMonth());
            if (ed.getDate() < sd.getDate()) months--;
            newData.warrantyMonths = Math.max(0, months);
          }
        }
      }
      return newData;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (modalType === 'product') {
      if (!formData.productName?.trim()) newErrors.productName = 'Product name is required';
      if (!formData.brand?.trim()) newErrors.brand = 'Brand is required';
      if (!formData.categoryId) newErrors.categoryId = 'Category is required';
      if (!formData.purchaseDate) newErrors.purchaseDate = 'Purchase Date is required';
      
      if (formData.productType === 'SERIALIZED') {
        if (!formData.skuProductCodes?.length) newErrors.skuProductCodes = 'At least one SKU is required';
        if (!formData.warrantyStartDate) newErrors.warrantyStartDate = 'Warranty start date required';
        if (!formData.warrantyEndDate) newErrors.warrantyEndDate = 'Warranty end date required';
      } else if (formData.productType === 'NON_SERIAL') {
        if (formData.stockQty <= 0) newErrors.stockQty = 'Quantity must be greater than 0';
      }
    } else {
      if (!formData.categoryName?.trim()) newErrors.categoryName = 'Category name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSubmitError(null);
    
    if (!validateForm()) {
      setSubmitError('Please check the highlighted fields above; the request cannot proceed.');
      return;
    }

    setIsUploading(true);
    try {
      const payload = {
        ...formData,
        purchasePriceInr: parseFloat(formData.purchasePriceInr) || 0,
        stockQty: parseInt(formData.stockQty) || 0,
        warrantyMonths: parseInt(formData.warrantyMonths) || 0,
        gstPercent: parseFloat(formData.gstPercent) || 0,
        categoryId: parseInt(formData.categoryId),
        dealerSupplierName: formData.dealerSupplierName?.trim() || 'Internal Stock',
      };
      
      // Delete frontend-only fields that crash the backend schema
      delete payload.sellingPriceInr;
      
      // Remove empty strings for dates/optional fields to prevent Jackson deserialization errors
      Object.keys(payload).forEach(key => {
         if (payload[key] === '') {
             payload[key] = null;
         }
      });

      if (modalType === 'product') {
        await onAdd(payload);
        setIsSuccess(true);
      } else {
        await onAdd({ categoryName: formData.categoryName, description: formData.description });
        setIsSuccess(true);
      }


    } catch (err) {
      console.error('Submission failed:', err);
      const serverErrors = applyServerErrors(err);
      const msg = serverErrors._form || err.response?.data?.message || err.message || "Critical failure during asset deployment.";
      setSubmitError(msg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setIsUploading(true);
    setUploadError(null);
    setUploadResult(null);
    try {
      const response = await inventoryAPI.bulkUploadProducts(bulkFile);
      setUploadResult(response.data);
      if (response.data.failureCount === 0) {
        if (onAdd) onAdd();
        setIsSuccess(true);
      } else {
        setUploadSuccess(true);
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Critical failure during bulk processing.";
      setUploadError(msg);
    } finally {
      setIsUploading(false);
    }
  };



  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className={cn(
        "max-h-[95vh] overflow-hidden flex flex-col p-0 transition-all duration-500 rounded-[3rem] border-0 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950",
        isSuccess ? "max-w-md" : "max-w-[1240px]"
      )}>
        <div className="flex-1 overflow-y-auto min-h-0 relative">
          {isSuccess ? (
            <SuccessScreen 
                message={modalType === 'product' ? "Inventory Secured!" : "Category Established!"}
                subMessage="Synchronizing ledger and returning..."
                countdown={countdown}
                onAction={() => setCountdown(0)}
            />
          ) : (
            <form id="productEntryForm" onSubmit={handleSubmit} className="contents relative">
              {/* Premium Sticky Header */}
              <div className="sticky top-0 z-50 px-8 py-6 bg-white/60 dark:bg-slate-900/60 dark:border-slate-800 backdrop-blur-3xl border-b border-white hover:bg-white/70 dark:hover:bg-slate-900/70 transition-colors">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-center gap-5">
                    <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-[#FF7D00] to-orange-400 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                        <div className="relative p-3.5 rounded-2xl bg-[#FF7D00]/10 text-[#FF7D00] shadow-sm transform hover:rotate-3 transition-transform duration-300">
                        {modalType === 'product' ? <Package className="w-8 h-8" /> : <Layers className="w-8 h-8" />}
                        </div>
                    </div>
                    <div>
                        <DialogTitle className="text-3xl font-black tracking-tight text-slate-800 dark:text-white flex items-center gap-2">
                            {step === 'choice' ? 'Inventory Options' :
                            step === 'bulk' ? 'Bulk Upload' :
                            initialData ? `Edit ${modalType === 'product' ? 'Product' : 'Category'}` : 
                            `Add ${modalType === 'product' ? 'New Product' : 'New Category'}`}
                            <span className="text-sm font-black px-3 py-1 bg-[#FF7D00]/10 text-[#FF7D00] rounded-full uppercase tracking-widest ml-1 shadow-sm">Enterprise</span>
                        </DialogTitle>
                        <DialogDescription className="text-sm font-bold text-slate-500 dark:text-slate-400 mt-1 opacity-80">
                            {step === 'choice' ? "Select your deployment mechanism." : 
                             step === 'bulk' ? "Importing mass assets via blueprint." :
                             "Precision single-entity registration."}
                        </DialogDescription>
                    </div>
                    </div>

                    {step !== 'choice' && (
                        <Button type="button" variant="ghost" onClick={() => setStep('choice')} className="rounded-xl h-12 px-6 font-bold text-slate-500 dark:text-slate-400 hover:text-[#FF7D00] hover:bg-orange-50 dark:hover:bg-slate-800 transition-all">
                            <History className="w-4 h-4 mr-2" /> Back to Choice
                        </Button>
                    )}
                </div>

                {submitError && (
                  <div className="mt-6 animate-in slide-in-from-top-4 duration-300">
                    <BeautifiedErrorAlert 
                        error={submitError} 
                        onDismiss={() => setSubmitError(null)} 
                    />
                  </div>
                )}
              </div>

              {/* Dynamic Content Body */}
              <div className="p-8 pb-32">
                {step === 'choice' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 py-12 animate-in fade-in zoom-in-95 duration-500">
                    <button
                      type="button"
                      onClick={() => setStep('bulk')}
                      className="group relative flex flex-col items-center justify-center p-12 rounded-[2.5rem] border-2 border-dashed border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 transition-all gap-6 active:scale-[0.98] overflow-hidden shadow-2xl shadow-transparent hover:shadow-indigo-500/10"
                    >
                      <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-bl-[100px] -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-700"></div>
                      <div className="p-7 rounded-3xl bg-indigo-50 text-indigo-500 group-hover:scale-110 group-hover:bg-indigo-500 group-hover:text-white transition-all duration-500 shadow-xl group-hover:rotate-6">
                        <FileSpreadsheet className="w-14 h-14" />
                      </div>
                      <div className="text-center relative z-10">
                        <h3 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight">Bulk Upload</h3>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-2 max-w-[220px]">Deploy massive inventory updates via Excel blueprints</p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setStep('form')}
                      className="group relative flex flex-col items-center justify-center p-12 rounded-[2.5rem] border-2 border-dashed border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/40 transition-all gap-6 active:scale-[0.98] overflow-hidden shadow-2xl shadow-transparent hover:shadow-emerald-500/10"
                    >
                      <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-bl-[100px] -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-700"></div>
                      <div className="p-7 rounded-3xl bg-emerald-50 text-emerald-500 group-hover:scale-110 group-hover:bg-emerald-500 group-hover:text-white transition-all duration-500 shadow-xl group-hover:-rotate-6">
                        <Plus className="w-14 h-14" />
                      </div>
                      <div className="text-center relative z-10">
                        <h3 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight">Direct Entry</h3>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-2 max-w-[220px]">Precision single-item registration system</p>
                      </div>
                    </button>
                  </div>
                )}

                {step === 'bulk' && (
                  <div className="space-y-8 animate-in slide-in-from-bottom-6 duration-500">
                    {!uploadSuccess ? (
                      <div className="space-y-6">
                        <div
                          className={cn(
                            "relative border-4 border-dashed rounded-[2.5rem] p-16 flex flex-col items-center justify-center transition-all cursor-pointer",
                            bulkFile ? "border-indigo-400 bg-indigo-50/30 ring-4 ring-indigo-500/5" : "border-slate-200 hover:border-indigo-300 hover:bg-slate-50/50"
                          )}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const file = e.dataTransfer.files[0];
                            if (file) setBulkFile(file);
                          }}
                        >
                          <input
                            type="file"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            onChange={(e) => setBulkFile(e.target.files[0])}
                            accept=".xlsx,.xls"
                          />
                          <div className={cn(
                            "p-6 rounded-3xl mb-6 shadow-xl transform transition-all duration-500",
                            bulkFile ? "bg-indigo-500 text-white scale-110 rotate-6" : "bg-slate-100 text-slate-400 group-hover:scale-105"
                          )}>
                            {bulkFile ? <FileUp className="w-12 h-12" /> : <Upload className="w-12 h-12" />}
                          </div>
                          <div className="text-center">
                            <p className="font-black text-2xl text-slate-800 dark:text-white tracking-tight">
                              {bulkFile ? bulkFile.name : 'Drop Spreadsheet Here'}
                            </p>
                            <p className="text-sm font-bold text-slate-500 dark:text-slate-400 mt-2 opacity-70 italic">Accepts .xlsx and .xls professional exports</p>
                          </div>
                          {bulkFile && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setBulkFile(null); }}
                              className="absolute top-8 right-8 p-2 rounded-full bg-slate-200/50 hover:bg-rose-100 hover:text-rose-600 transition-all active:scale-90"
                            >
                              <X className="w-6 h-6" />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-sky-50/50 border border-sky-100 rounded-3xl p-6 flex gap-5">
                                <Info className="w-8 h-8 text-sky-500 shrink-0 mt-1" />
                                <div className="space-y-3">
                                    <h4 className="text-base font-black text-sky-900 uppercase tracking-wider">Blueprint Guidelines</h4>
                                    <ul className="text-sm text-sky-800/80 space-y-2 font-medium list-disc list-inside">
                                        <li>Ensure <span className="text-sky-900 font-bold underline">HSN Codes</span> are 8 digits</li>
                                        <li>Multiple SKUs? Separate them by <span className="font-black">commas (,)</span></li>
                                        <li>Date format: <span className="font-black">DD-MM-YYYY</span> required</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 flex items-center justify-between group hover:bg-white transition-all hover:shadow-lg hover:shadow-black/5">
                                <div className="flex items-center gap-4">
                                    <div className="p-4 bg-emerald-100 text-emerald-600 rounded-2xl group-hover:rotate-6 transition-transform">
                                        <FileSpreadsheet className="w-8 h-8" />
                                    </div>
                                    <div>
                                        <p className="font-black text-slate-800 dark:text-white">Excel Template</p>
                                        <p className="text-sm font-bold text-slate-500 dark:text-slate-400">Official JIT Protocol format</p>
                                    </div>
                                </div>
                                <Button type="button" variant="outline" size="lg" onClick={() => window.open('/productTemplate.xlsx')} 
                                    className="rounded-xl border-emerald-200 text-emerald-600 hover:bg-emerald-600 hover:text-white font-black px-6 transition-all shadow-md">
                                    Download
                                </Button>
                            </div>
                        </div>

                        {uploadError && (
                            <BeautifiedErrorAlert error={uploadError} onDismiss={() => setUploadError(null)} />
                        )}
                      </div>
                    ) : (
                      /* Bulk Upload Results View */
                      <div className="py-8 text-center space-y-8 animate-in zoom-in-95 duration-500">
                         <div className="relative inline-block">
                            <div className="absolute -inset-4 bg-amber-500 rounded-full blur-2xl opacity-20 animate-pulse"></div>
                            <div className="relative p-8 rounded-full bg-amber-50 text-amber-500 border-4 border-amber-100 shadow-xl">
                                <History className="w-20 h-20" />
                            </div>
                         </div>
                         <div>
                            <h3 className="text-4xl font-black text-slate-800 tracking-tight italic">Processing Report</h3>
                            <p className="text-lg font-bold text-slate-500 mt-2 opacity-80">
                              Analyzed <span className="text-slate-900 px-2 py-0.5 bg-slate-100 rounded-lg">{uploadResult.totalProcessed}</span> entries in record time.
                            </p>
                         </div>
                         <div className="grid grid-cols-2 gap-8 max-w-xl mx-auto">
                            <div className="p-8 bg-emerald-50 rounded-[2.5rem] border-2 border-emerald-100 shadow-lg shadow-emerald-500/5 group hover:scale-105 transition-transform duration-500">
                               <p className="text-5xl font-black text-emerald-600 mb-2">{uploadResult.successCount}</p>
                               <p className="text-sm font-black text-emerald-700 uppercase tracking-[0.2em]">Verified</p>
                            </div>
                            <div className="p-8 bg-rose-50 rounded-[2.5rem] border-2 border-rose-100 shadow-lg shadow-rose-500/5 group hover:scale-105 transition-transform duration-500">
                               <p className="text-5xl font-black text-rose-600 mb-2">{uploadResult.failureCount}</p>
                               <p className="text-sm font-black text-rose-700 uppercase tracking-[0.2em]">Rejected</p>
                            </div>
                         </div>
                         {uploadResult.errors?.length > 0 && (
                           <div className="text-left bg-white rounded-3xl border shadow-2xl border-rose-100 overflow-hidden mt-8 max-w-2xl mx-auto ring-1 ring-rose-500/10">
                              <div className="px-6 py-4 bg-rose-50 border-b border-rose-100 font-black text-rose-700 uppercase tracking-widest text-[10px] flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" /> Failure Manifest (Row Analysis)
                              </div>
                              <div className="max-h-[300px] overflow-y-auto p-6 space-y-3 custom-scrollbar">
                                 {uploadResult.errors.map((e, i) => (
                                   <div key={i} className="text-sm flex gap-4 p-3 bg-slate-50 rounded-xl border border-slate-100 hover:bg-rose-50 transition-colors">
                                      <span className="text-rose-600 font-black shrink-0">Row {e.rowNumber}:</span>
                                      <span className="text-slate-600 font-bold">{e.error}</span>
                                   </div>
                                 ))}
                              </div>
                           </div>
                         )}
                         <div className="pt-4">
                            <Button onClick={() => setUploadSuccess(false)} variant="outline" className="rounded-2xl h-14 px-10 font-black uppercase tracking-widest text-[#ff6f00] border-orange-200">
                                Try Another Sync
                            </Button>
                         </div>
                      </div>
                    )}
                  </div>
                )}

                {step === 'form' && (
                  <div className="space-y-16 animate-in slide-in-from-bottom-8 duration-700">
                    {modalType === 'product' ? (
                      /* Professional Product Form */
                      <div className="space-y-16">
                         {/* Product Identity Card */}
                        <div className="bg-white rounded-[2.5rem] p-1 shadow-sm border border-slate-100">
                          <div className="p-8 space-y-8">
                             <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-2xl bg-[#FF7D00]/10 text-[#FF7D00] shadow-inner">
                                    <Tag className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-black text-slate-800 dark:text-white tracking-tight uppercase tracking-widest">Identity & Cataloging</h3>
                             </div>
                             
                             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                <div className="space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-[#FF7D00] ml-1">
                                    Category <span className="text-red-500">*</span>
                                    </Label>
                                    <div className="flex gap-2">
                                    <div className="flex-1 min-w-0">
                                        <CategoryDropdown
                                            value={formData.categoryId}
                                            onChange={(id, name) => {
                                                setFormData(prev => ({ ...prev, categoryId: id, categoryName: name }));
                                                if (errors.categoryId) setErrors(prev => ({ ...prev, categoryId: null }));
                                            }}
                                            placeholder="Select Category"
                                            categories={categories}
                                            loading={categoriesLoading}
                                            hasMore={hasMoreCategories}
                                            onLoadMore={() => fetchCategories(categoriesPage + 1)}
                                            error={errors.categoryId}
                                        />
                                    </div>
                                    <Button type="button" variant="outline" size="icon" onClick={() => setShowInlineCategoryModal(true)}
                                        className="h-14 w-14 shrink-0 rounded-2xl bg-white border-slate-200 text-[#ff6f00] hover:bg-[#ff6f00] hover:text-white transition-all shadow-md active:scale-90">
                                        <Plus className="w-6 h-6" />
                                    </Button>
                                    </div>
                                    {errors.categoryId && <p className="text-[10px] font-black text-rose-600 ml-2 uppercase tracking-tight">{errors.categoryId}</p>}
                                </div>

                                <div className="space-y-3">
                                    <Label htmlFor="productName" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Product Name <span className="text-rose-500">*</span></Label>
                                    <Input
                                        id="productName"
                                        name="productName"
                                        value={formData.productName}
                                        onChange={handleInputChange}
                                        className={cn("jit-input h-14 text-lg", errors.productName && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                                        placeholder="ThinkPad X1 Carbon"
                                    />
                                    {errors.productName && <p className="text-[10px] font-black text-red-600 ml-2 uppercase tracking-tight">Required</p>}
                                </div>

                                <div className="space-y-3">
                                    <Label htmlFor="brand" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Brand <span className="text-rose-500">*</span></Label>
                                    <Input
                                        id="brand"
                                        name="brand"
                                        value={formData.brand}
                                        onChange={handleInputChange}
                                        className={cn("jit-input h-14 text-lg", errors.brand && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                                        placeholder="Lenovo"
                                    />
                                    {errors.brand && <p className="text-[10px] font-black text-red-600 ml-2 uppercase tracking-tight">Required</p>}
                                </div>
                             </div>
                          </div>
                        </div>

                        {/* Stock & Logistics Card */}
                        <div className="bg-white rounded-[2.5rem] p-1 shadow-sm border border-slate-100">
                          <div className="p-8 space-y-8">
                             <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-2xl bg-amber-50 text-amber-600 shadow-inner">
                                    <LayoutGrid className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase tracking-widest">Workflow & Inventory Type</h3>
                             </div>
                             
                             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                <div className="space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Product Model/Type</Label>
                                    <Select
                                        value={formData.productType}
                                        onValueChange={(v) => {
                                            setFormData(prev => ({ 
                                            ...prev, 
                                            productType: v,
                                            skuProductCodes: v === 'SERIALIZED' ? prev.skuProductCodes : [],
                                            stockQty: v === 'SERIALIZED' ? prev.skuProductCodes.length : prev.stockQty 
                                            }));
                                        }}
                                    >
                                        <SelectTrigger className="h-14 rounded-2xl border-slate-200 font-bold px-5">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl p-2 border-slate-200">
                                            <SelectItem value="SERIALIZED" className="rounded-xl h-12 font-bold focus:bg-orange-50 focus:text-orange-600">Serialized (SKU Protocol)</SelectItem>
                                            <SelectItem value="NON_SERIAL" className="rounded-xl h-12 font-bold focus:bg-orange-50 focus:text-orange-600">Non-Serialized (Bulk)</SelectItem>
                                            <SelectItem value="SERVICE" className="rounded-xl h-12 font-bold focus:bg-orange-50 focus:text-orange-600">Service Engagement</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-3">
                                    <Label htmlFor="hsnCode" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">HSN Code <span className="text-slate-300 font-medium normal-case">(optional)</span></Label>
                                    <Input id="hsnCode" name="hsnCode" value={formData.hsnCode} onChange={handleInputChange} className="h-14 rounded-2xl border-slate-200 font-black tracking-[0.3em] px-5 uppercase" placeholder="8DIGITS" maxLength={8} />
                                </div>

                                <div className="space-y-3">
                                    <Label htmlFor="stockQty" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Current Availability</Label>
                                    <Input
                                        id="stockQty"
                                        name="stockQty"
                                        type="number"
                                        value={formData.stockQty}
                                        onChange={handleInputChange}
                                        readOnly={formData.productType === 'SERIALIZED'}
                                        className={cn(
                                            "h-14 rounded-2xl border-slate-200 font-black text-xl px-5 transition-all focus:ring-4 focus:ring-indigo-500/10",
                                            formData.productType === 'SERIALIZED' && "bg-slate-50/80 text-indigo-600 opacity-100 border-dashed border-indigo-200 cursor-not-allowed",
                                            errors.stockQty && "border-rose-400 bg-rose-50/50 ring-4 ring-rose-500/10"
                                        )}
                                    />
                                    {errors.stockQty && <p className="text-[10px] font-black text-rose-600 ml-2 uppercase tracking-tight">{errors.stockQty}</p>}
                                </div>

                                {formData.productType === 'SERIALIZED' && (
                                    <div className="md:col-span-2 lg:col-span-3 space-y-4 pt-4 border-t border-slate-50">
                                        <div className="flex items-center justify-between px-2">
                                            <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#ff6f00]">SKU Fingerprint Verification</Label>
                                            <span className="text-xs font-black px-3 py-1 bg-orange-100 text-[#ff6f00] rounded-lg">Scan & Entry Required</span>
                                        </div>
                                        <div className="p-1.5 rounded-3xl bg-slate-50 border-2 border-slate-100 shadow-inner overflow-hidden ring-4 ring-slate-100/50">
                                            <SKUPillInput
                                                value={formData.skuProductCodes}
                                                onChange={(s) => {
                                                    setFormData(p => ({ ...p, skuProductCodes: s, stockQty: s.length }));
                                                    if (errors.skuProductCodes) setErrors(prev => ({ ...prev, skuProductCodes: null }));
                                                }}
                                                error={errors.skuProductCodes}
                                                placeholder="Inject SKUs into the system... [Enter after each]"
                                            />
                                        </div>
                                        {errors.skuProductCodes && <p className="text-[11px] font-black text-rose-600 ml-4 animate-bounce uppercase tracking-tight">{errors.skuProductCodes}</p>}
                                    </div>
                                )}
                             </div>
                          </div>
                        </div>

                         {/* Financials Card */}
                        <div className="bg-white rounded-[2.5rem] p-1 shadow-sm border border-slate-100">
                          <div className="p-8 space-y-8">
                             <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-2xl bg-emerald-50 text-emerald-600 shadow-inner">
                                    <Calendar className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase tracking-widest">Financials & Logistics</h3>
                             </div>
                             
                             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div className="space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Purchase Price (₹)</Label>
                                    <Input name="purchasePriceInr" type="number" step="0.01" value={formData.purchasePriceInr} onChange={handleInputChange} className="h-12 rounded-xl font-bold border-slate-200" placeholder="0.00" />
                                </div>
                                <div className="space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Selling Price (₹)</Label>
                                    <Input name="sellingPriceInr" type="number" step="0.01" value={formData.sellingPriceInr} onChange={handleInputChange} className="h-12 rounded-xl font-black text-emerald-600 border-slate-200" placeholder="0.00" />
                                </div>
                                <div className="space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">GST Percent (%)</Label>
                                    <Input name="gstPercent" type="number" step="1" value={formData.gstPercent} onChange={handleInputChange} className="h-12 rounded-xl font-bold border-slate-200" placeholder="18" />
                                 </div>
                                 <div className="space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Purchase Date <span className="text-rose-500">*</span></Label>
                                    <Input 
                                        name="purchaseDate" 
                                        type="date" 
                                        value={formData.purchaseDate} 
                                        onChange={handleInputChange} 
                                        className={cn(
                                            "h-12 rounded-xl font-bold transition-all", 
                                            errors.purchaseDate ? "border-rose-400 bg-rose-50 ring-4 ring-rose-500/10" : "border-slate-200"
                                        )} 
                                    />
                                    {errors.purchaseDate && <p className="text-[10px] font-black text-rose-600 ml-2 uppercase tracking-tight">{errors.purchaseDate}</p>}
                                </div>

                                <div className="space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Warranty Start {formData.productType === 'SERIALIZED' && '*'}</Label>
                                    <Input 
                                        name="warrantyStartDate" 
                                        type="date" 
                                        value={formData.warrantyStartDate} 
                                        onChange={handleInputChange} 
                                        className={cn(
                                            "h-12 rounded-xl font-bold transition-all", 
                                            errors.warrantyStartDate ? "border-rose-400 bg-rose-50 ring-4 ring-rose-500/10" : "border-slate-200"
                                        )} 
                                    />
                                    {errors.warrantyStartDate && <p className="text-[10px] font-black text-rose-600 ml-2 uppercase tracking-tight">{errors.warrantyStartDate}</p>}
                                </div>

                                <div className="space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Warranty End {formData.productType === 'SERIALIZED' && '*'}</Label>
                                    <Input 
                                        name="warrantyEndDate" 
                                        type="date" 
                                        value={formData.warrantyEndDate} 
                                        onChange={handleInputChange} 
                                        className={cn(
                                            "h-12 rounded-xl font-bold transition-all", 
                                            errors.warrantyEndDate ? "border-rose-400 bg-rose-50 ring-4 ring-rose-500/10" : "border-slate-200"
                                        )} 
                                    />
                                    {errors.warrantyEndDate && <p className="text-[10px] font-black text-rose-600 ml-2 uppercase tracking-tight">{errors.warrantyEndDate}</p>}
                                </div>

                                <div className="space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-indigo-600 ml-1">Total Months</Label>
                                    <Input value={formData.warrantyMonths} readOnly className="h-12 rounded-xl bg-indigo-50/50 border-0 font-black text-center text-indigo-700" />
                                </div>
                                <div className="space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Location/Rack</Label>
                                    <Input name="locationRack" value={formData.locationRack} onChange={handleInputChange} className="h-12 rounded-xl font-bold border-slate-200 uppercase" placeholder="A1-12" />
                                </div>
                                <div className="md:col-span-2 lg:col-span-4 space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Dealer/Supplier Name</Label>
                                    <Input name="dealerSupplierName" value={formData.dealerSupplierName} onChange={handleInputChange} className="h-12 rounded-xl font-bold border-slate-200" placeholder="e.g. ABC Wholesalers Pvt Ltd" />
                                </div>
                             </div>
                          </div>
                        </div>

                        {/* Descriptions & Specs Card */}
                        <div className="bg-white rounded-[2.5rem] p-1 shadow-sm border border-slate-100">
                           <div className="p-8 space-y-8">
                             <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-2xl bg-slate-50 text-slate-600 shadow-inner">
                                    <ClipboardList className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase tracking-widest">Documentation & Technicals</h3>
                             </div>

                             <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                <div className="space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Product Description</Label>
                                    <Textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Summarize the core features and condition..." className="rounded-3xl min-h-[140px] p-5 font-medium border-slate-200 shadow-inner" />
                                </div>
                                <div className="space-y-3">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Technical Specs</Label>
                                    <Textarea name="specification" value={formData.specification} onChange={handleInputChange} placeholder="CPU: i9 12th Gen | RAM: 64GB DDR5 | STORAGE: 2TB NVMe..." className="rounded-3xl min-h-[140px] p-5 font-black text-slate-600 leading-relaxed border-slate-200 bg-slate-50/30" />
                                </div>
                             </div>
                           </div>
                        </div>
                      </div>
                    ) : (
                      /* Category Specialized Form */
                      <div className="max-w-xl mx-auto space-y-12 py-12 animate-in slide-in-from-bottom-5">
                         <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-[#ff6f00] to-orange-400 rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-1000"></div>
                            <div className="relative space-y-3 bg-white p-8 rounded-3xl border border-white shadow-xl">
                                <Label className="text-sm font-black text-slate-800 uppercase tracking-widest ml-1">Category Nomenclature <span className="text-rose-500">*</span></Label>
                                <Input
                                    name="categoryName"
                                    value={formData.categoryName}
                                    onChange={handleInputChange}
                                    placeholder="Enter Category Label..."
                                    className={cn("jit-input h-16 text-2xl", errors.categoryName ? "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake" : "border-slate-100 dark:border-slate-800")}
                                    autoFocus
                                />
                                {errors.categoryName && <p className="text-xs font-black text-red-600 ml-2 uppercase tracking-tight">Required</p>}
                            </div>
                         </div>

                         <div className="space-y-3 bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                            <Label className="text-[11px] font-black text-slate-500 uppercase tracking-widest ml-1">Architectural Context</Label>
                            <Textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Define the operational scope of this category module..."
                                className="rounded-3xl min-h-[200px] p-8 text-lg font-bold text-slate-600 border-slate-100 bg-slate-50/20"
                            />
                         </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Global Action Terminal */}
        {!isSuccess && (
            <DialogFooter className="p-8 border-t bg-white/80 backdrop-blur-xl sticky bottom-0 z-[60] flex-col sm:flex-row gap-6 items-center sm:justify-between shadow-[0_-10px_30px_-15px_rgba(0,0,0,0.1)] rounded-b-[3rem]">
                <div className="w-full sm:w-auto flex items-center gap-4">
                    {step === 'form' && !initialData && (
                        <Button type="button" variant="ghost" onClick={handleClearDraft} className="h-14 px-10 rounded-2xl font-black text-rose-500 uppercase tracking-widest hover:bg-rose-50 transition-all flex-1 sm:flex-none">
                            Clear
                        </Button>
                    )}
                    
                    <Button type="button" variant="ghost" onClick={onClose} className="h-14 px-10 rounded-2xl font-black text-slate-400 uppercase tracking-widest hover:bg-slate-50 transition-all flex-1 sm:flex-none">
                        Close
                    </Button>
                    
                    {step === 'bulk' && !uploadSuccess && (
                        <Button 
                            type="button"
                            onClick={handleBulkUpload} 
                            disabled={!bulkFile || isUploading}
                            className={cn(
                                "h-14 px-12 rounded-2xl font-black uppercase tracking-widest transition-all flex-1 sm:flex-none shadow-2xl",
                                bulkFile ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-200" : "bg-slate-200 text-slate-400 cursor-not-allowed"
                            )}
                        >
                            {isUploading ? <><Loader2 className="w-5 h-5 mr-3 animate-spin" /> Verifying</> : <><FileUp className="w-5 h-5 mr-3" /> Commit Sync</>}
                        </Button>
                    )}

                    {step === 'form' && (
                        <Button 
                            form="productEntryForm" 
                            type="submit" 
                            disabled={isUploading}
                            className="h-14 px-16 rounded-2xl bg-[#ff6f00] hover:bg-[#e66400] text-white font-black uppercase tracking-[0.2em] shadow-2xl shadow-orange-500/30 flex-1 sm:flex-none transition-all active:scale-95 group"
                        >
                            {isUploading ? (
                                <><Loader2 className="w-5 h-5 mr-3 animate-spin" /> Transmitting</>
                            ) : (
                                <>{initialData ? 'Update Record' : 'Add Product'} <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1.5 transition-transform" /></>
                            )}
                        </Button>
                    )}
                </div>
            </DialogFooter>
        )}
      </DialogContent>

      {/* Inline Quick Category Add Component */}
      <Dialog open={showInlineCategoryModal} onOpenChange={setShowInlineCategoryModal}>
        <DialogContent className="sm:max-w-[450px] p-0 overflow-hidden rounded-[2.5rem] border-0 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] ring-1 ring-black/5">
          <DialogHeader className="p-8 bg-gradient-to-br from-orange-50 to-white border-b border-orange-100">
             <div className="flex items-center gap-4">
                <div className="p-3 rounded-2xl bg-orange-100 text-[#ff6f00] shadow-sm">
                    <Layers className="w-6 h-6" />
                </div>
                <div>
                   <DialogTitle className="text-2xl font-black text-slate-800 tracking-tight">Quick Catalog</DialogTitle>
                   <DialogDescription className="font-bold text-orange-600/70 text-xs uppercase tracking-widest mt-1">System Labeling Protocol</DialogDescription>
                </div>
             </div>
          </DialogHeader>
          <div className="p-10 space-y-8 bg-white">
            <div className="space-y-4">
              <Label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Category Nomenclature</Label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Hardware, Peripherals etc."
                className="h-16 rounded-2xl border-slate-200 font-bold text-xl px-6 focus:ring-4 focus:ring-orange-500/10 transition-all"
                autoFocus
              />
            </div>
            
            <div className="flex gap-4 pt-2">
                <Button variant="ghost" onClick={() => { setShowInlineCategoryModal(false); setNewCategoryName(''); }} className="h-14 flex-1 rounded-2xl font-bold text-slate-400 hover:bg-slate-50 transition-all">
                    Discard
                </Button>
                <Button
                    className="h-14 flex-[1.5] rounded-2xl bg-[#ff6f00] hover:bg-[#e66400] text-white font-black uppercase tracking-widest shadow-xl shadow-orange-500/20 active:scale-95 transition-all"
                    disabled={isAddingCategory || !newCategoryName.trim()}
                    onClick={async () => {
                        try {
                            setIsAddingCategory(true);
                            const res = await inventoryAPI.createCategory({ categoryName: newCategoryName });
                            await fetchCategories(0, true);
                            setFormData(p => ({ ...p, categoryId: res.data.categoryId, categoryName: res.data.categoryName }));
                            setSubmitError(null);
                            setShowInlineCategoryModal(false);
                            setNewCategoryName('');
                        } catch (e) {
                           const serverErrors = applyServerErrors(e);
                           setSubmitError(serverErrors._form || e.response?.data?.message || "Error adding category");
                        } finally {
                           setIsAddingCategory(false);
                        }
                    }}
                >
                    {isAddingCategory ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Confirm Entity'}
                </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default AddModal;
